/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Review System API Client
 * 
 * Client-side functions for interacting with the review system API endpoints.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import { ReviewSession, ReviewComment, ReviewSchema } from '@shared/schema/review';
import { 
  CreateReviewSessionRequestData,
  UpdateReviewSessionRequestData,
  CreateReviewCommentRequestData,
  CreateReviewSchemaRequestData,
  UpdateReviewSchemaRequestData
} from '@shared/types/requests/review';

// Review Sessions

/**
 * Get review sessions with optional filtering
 */
export async function getReviewSessions(filters?: {
  userId?: number;
  status?: string;
  agentId?: number;
  limit?: number;
  offset?: number;
}): Promise<ReviewSession[]> {
  const params = new URLSearchParams();
  
  if (filters?.userId) params.append('userId', filters.userId.toString());
  if (filters?.status) params.append('status', filters.status);
  if (filters?.agentId) params.append('agentId', filters.agentId.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());

  const response = await authenticatedFetch(`/api/review/sessions?${params.toString()}`);
  return handleApiResponse<ReviewSession[]>(response);
}

/**
 * Get a specific review session by ID
 */
export async function getReviewSession(id: string): Promise<ReviewSession> {
  const response = await authenticatedFetch(`/api/review/sessions/${id}`);
  return handleApiResponse<ReviewSession>(response);
}

/**
 * Create a new review session
 */
export async function createReviewSession(data: CreateReviewSessionRequestData): Promise<ReviewSession> {
  const response = await authenticatedFetch('/api/review/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleApiResponse<ReviewSession>(response);
}

/**
 * Update a review session
 */
export async function updateReviewSession(
  id: string, 
  updates: UpdateReviewSessionRequestData
): Promise<ReviewSession> {
  const response = await authenticatedFetch(`/api/review/sessions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return handleApiResponse<ReviewSession>(response);
}

/**
 * Delete a review session
 */
export async function deleteReviewSession(id: string): Promise<{ deleted: boolean }> {
  const response = await authenticatedFetch(`/api/review/sessions/${id}`, {
    method: 'DELETE',
  });
  return handleApiResponse<{ deleted: boolean }>(response);
}

// Review Comments

/**
 * Get comments for a review session
 */
export async function getReviewComments(sessionId: string): Promise<ReviewComment[]> {
  const response = await authenticatedFetch(`/api/review/sessions/${sessionId}/comments`);
  return handleApiResponse<ReviewComment[]>(response);
}

/**
 * Add a comment to a review session
 */
export async function createReviewComment(
  sessionId: string, 
  data: CreateReviewCommentRequestData
): Promise<ReviewComment> {
  const response = await authenticatedFetch(`/api/review/sessions/${sessionId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleApiResponse<ReviewComment>(response);
}

/**
 * Delete a comment
 */
export async function deleteReviewComment(id: number): Promise<{ deleted: boolean }> {
  const response = await authenticatedFetch(`/api/review/comments/${id}`, {
    method: 'DELETE',
  });
  return handleApiResponse<{ deleted: boolean }>(response);
}

// Review Schemas

/**
 * Get review schemas with optional filtering
 */
export async function getReviewSchemas(filters?: {
  isActive?: boolean;
  createdBy?: number;
  limit?: number;
}): Promise<ReviewSchema[]> {
  const params = new URLSearchParams();
  
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  if (filters?.createdBy) params.append('createdBy', filters.createdBy.toString());
  if (filters?.limit) params.append('limit', filters.limit.toString());

  const response = await authenticatedFetch(`/api/review/schemas?${params.toString()}`);
  return handleApiResponse<ReviewSchema[]>(response);
}

/**
 * Get a specific review schema by ID
 */
export async function getReviewSchema(id: string): Promise<ReviewSchema> {
  const response = await authenticatedFetch(`/api/review/schemas/${id}`);
  return handleApiResponse<ReviewSchema>(response);
}

/**
 * Create a new review schema
 */
export async function createReviewSchema(data: CreateReviewSchemaRequestData): Promise<ReviewSchema> {
  const response = await authenticatedFetch('/api/review/schemas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return handleApiResponse<ReviewSchema>(response);
}

/**
 * Update a review schema
 */
export async function updateReviewSchema(
  id: string, 
  updates: UpdateReviewSchemaRequestData
): Promise<ReviewSchema> {
  const response = await authenticatedFetch(`/api/review/schemas/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  return handleApiResponse<ReviewSchema>(response);
}

/**
 * Delete a review schema
 */
export async function deleteReviewSchema(id: string): Promise<{ deleted: boolean }> {
  const response = await authenticatedFetch(`/api/review/schemas/${id}`, {
    method: 'DELETE',
  });
  return handleApiResponse<{ deleted: boolean }>(response);
}

// Agent Execution Termination

/**
 * Terminate an agent execution
 */
export async function terminateAgentExecution(
  executionId: string, 
  reason: string
): Promise<{ terminated: boolean; executionId: string }> {
  const response = await authenticatedFetch(`/api/agent/executions/${executionId}/terminate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return handleApiResponse<{ terminated: boolean; executionId: string }>(response);
}

/**
 * Get termination status for an execution
 */
export async function getTerminationStatus(executionId: string): Promise<{
  canTerminate: boolean;
  status: string;
  reason?: string;
}> {
  const response = await authenticatedFetch(`/api/agent/executions/${executionId}/termination-status`);
  return handleApiResponse<{ canTerminate: boolean; status: string; reason?: string }>(response);
}
